import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCRMAuth } from "@/contexts/CRMAuthContext";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Users, Mail, BarChart3, Target, MessageSquare, Calendar,
  CheckCircle, ArrowRight, UserPlus, Zap, Shield
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const CRMLandingPage = () => {
  const { crmUser, getToken } = useCRMAuth();
  const navigate = useNavigate();
  const [seats, setSeats] = useState(5);
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async () => {
    if (!crmUser) {
      navigate("/crm/register/business");
      return;
    }
    setSubscribing(true);
    try {
      const response = await fetch(`${API}/crm/org/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ seats, origin_url: window.location.origin }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to start checkout");
      }
    } catch { toast.error("Failed to connect"); }
    finally { setSubscribing(false); }
  };

  const features = [
    { icon: Users, title: "Contact Management", description: "Organize all your customers, leads, and contacts in one place with custom fields and tags." },
    { icon: Mail, title: "Email Campaigns", description: "Send targeted email campaigns to segments. Track opens, clicks, and conversions." },
    { icon: Target, title: "Deal Pipeline", description: "Visual pipeline to track deals from lead to close. Drag & drop stage management." },
    { icon: BarChart3, title: "Analytics & Reports", description: "Real-time dashboards showing sales performance, customer engagement, and team activity." },
    { icon: MessageSquare, title: "Customer Communication", description: "Unified inbox for all customer conversations. SMS, email, and in-app messaging." },
    { icon: Calendar, title: "Task & Activity Tracking", description: "Assign tasks, set reminders, and track follow-ups. Never miss a customer touchpoint." },
  ];

  const included = [
    "Unlimited contacts",
    "Email campaigns",
    "Deal pipeline",
    "Task management",
    "Customer segmentation",
    "Analytics dashboard",
    "Team collaboration",
    "API access",
    "Priority support",
    "Custom fields"
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/crm" className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="V" className="h-9 w-auto brightness-0 invert" />
              <div className="h-7 w-px bg-white/40" />
              <span className="text-white font-brand text-lg font-bold">VEYRONIX <span className="text-indigo-300 text-xs font-medium ml-1 align-middle">CRM</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/crm" className="text-white font-medium text-sm hover:text-indigo-300 transition-colors">Product</Link>
              <a href="#pricing" className="text-slate-300 text-sm hover:text-white transition-colors">Pricing</a>
              <a href="https://www.veyronix.co.uk" className="text-slate-300 text-sm hover:text-white transition-colors">Veyronix Marketplace ↗</a>
            </div>
            <div className="flex items-center gap-3">
              {crmUser ? (
                <Button onClick={() => navigate("/crm-app")} className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" data-testid="nav-go-app">
                  Open CRM
                </Button>
              ) : (
                <>
                  <Button onClick={() => navigate("/crm/login")} variant="ghost" className="text-white hover:bg-white/10" size="sm" data-testid="nav-crm-login">Sign In</Button>
                  <Button onClick={() => navigate("/crm/register")} className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" data-testid="nav-crm-register">Get Started</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero — Light with subtle indigo glow */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-slate-50">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-indigo-200/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-200/40 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-5xl mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-6">
            <Zap className="h-4 w-4 text-indigo-600" />
            <span className="text-indigo-700 text-sm font-medium">Powered by VEYRONIX</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6">
            CRM Built for<br />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">Growing Businesses</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10">
            Manage your customers, track deals, run campaigns, and grow your revenue — all from one powerful platform.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button onClick={() => navigate(crmUser ? "/crm-app" : "/crm/register")} size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12">
              {crmUser ? "Open CRM" : "Start Free Trial"}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button onClick={() => document.getElementById('pricing')?.scrollIntoView({behavior: 'smooth'})} variant="outline" size="lg" className="border-slate-300 text-slate-700 hover:bg-white hover:border-indigo-300 rounded-full px-8 h-12">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-heading font-bold text-slate-900 mb-4">Everything You Need to Grow</h2>
          <p className="text-slate-500 max-w-xl mx-auto">A complete CRM platform with tools to manage relationships, close deals, and scale your business.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-indigo-200 transition-all group">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4 group-hover:bg-indigo-600 transition-colors">
                <feature.icon className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-white py-20 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-heading font-bold text-slate-900 mb-4">Simple Per-Seat Pricing</h2>
            <p className="text-slate-500">One plan, all features included. Pay only for the seats you need.</p>
          </div>

          <div className="max-w-md mx-auto bg-gradient-to-br from-slate-900 to-indigo-900 rounded-2xl p-8 text-white shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-indigo-500/30 rounded-full px-3 py-1 mb-4">
                <Shield className="h-4 w-4 text-indigo-300" />
                <span className="text-xs text-indigo-200 font-medium">ALL FEATURES INCLUDED</span>
              </div>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold">£9.99</span>
                <span className="text-slate-300">/seat/month</span>
              </div>
            </div>

            {/* Seat Calculator */}
            <div className="bg-white/10 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-200">Team size</span>
                <span className="text-sm text-indigo-300 font-medium">{seats} seats</span>
              </div>
              <input
                type="range"
                min="1"
                max="50"
                value={seats}
                onChange={(e) => setSeats(parseInt(e.target.value))}
                className="w-full h-2 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-400"
                data-testid="seat-slider"
              />
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/20">
                <span className="text-slate-200">Monthly total</span>
                <span className="text-2xl font-bold text-white">£{(seats * 9.99).toFixed(2)}</span>
              </div>
            </div>

            {/* What's included */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              {included.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-xs text-slate-200">{item}</span>
                </div>
              ))}
            </div>

            <Button onClick={handleSubscribe} disabled={subscribing} className="w-full bg-indigo-500 hover:bg-indigo-400 text-white h-12 rounded-xl font-semibold" data-testid="crm-get-started-btn">
              {subscribing ? "Processing..." : "Get Started"}
              {!subscribing && <ArrowRight className="h-5 w-5 ml-2" />}
            </Button>
            <p className="text-center text-xs text-slate-400 mt-3">14-day free trial. No credit card required.</p>
          </div>
        </div>
      </section>

      {/* CTA — Light with indigo accent */}
      <section className="bg-gradient-to-br from-indigo-600 to-violet-700 py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-400/20 rounded-full blur-[80px]" />
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <h2 className="text-2xl font-heading font-bold text-white mb-4">Ready to transform your customer relationships?</h2>
          <p className="text-indigo-100 mb-8">Join thousands of businesses using <span className="font-brand">VEYRONIX</span> CRM.</p>
          <div className="flex items-center justify-center gap-4">
            <Button onClick={() => navigate(crmUser ? "/crm-app" : "/crm/register")} size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-full px-8 font-semibold" data-testid="cta-bottom-trial">
              {crmUser ? "Open CRM" : "Start Free Trial"}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8 text-center">
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} <span className="font-brand">VEYRONIX</span>. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default CRMLandingPage;
